import { useEffect, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { agentTasksApi } from "@/api/agent-tasks";
import { TASKS_QUERY_ROOT } from "@/components/tasks/task-list";

/**
 * Fetch the scheduled jobs an OpenClaw agent made on its own VM (OpenClaw has
 * its own cron) into agent_tasks, so every Tasks view shows them. Only
 * OpenClaw has such a store; the server no-ops every other engine
 * (`syncFromAgent` in agent-task-scheduler.ts), so the UI does not ask.
 *
 * Runs by itself each time a one-agent Tasks view opens (2026-10-09). It used
 * to run only in the agent settings Tasks tab, which standard users cannot
 * open, behind a "Sync from Agent" button; the button went once the check
 * became automatic. Never run it for a whole list of agents: each check asks
 * that agent's VM.
 */
export function useAutoTaskSync(agentId: string | null | undefined, adapterType: string | null | undefined) {
  const queryClient = useQueryClient();
  const lastSynced = useRef<string | null>(null);
  const [state, setState] = useState<"idle" | "syncing" | "failed">("idle");
  const enabled = !!agentId && adapterType === "openclaw_gateway";

  useEffect(() => {
    if (!enabled || lastSynced.current === agentId) return;
    lastSynced.current = agentId!;
    setState("syncing");
    agentTasksApi
      .syncFromAgent(agentId!)
      .then((result) => {
        // The server reports an unreachable agent as `error` in a 200 reply.
        setState(result.error ? "failed" : "idle");
        return queryClient.invalidateQueries({ queryKey: TASKS_QUERY_ROOT });
      })
      .catch(() => setState("failed"));
    // No unmount guard: the ref above stops a second run for the same agent,
    // so a guarded first run would leave the state stuck on "syncing".
  }, [enabled, agentId, queryClient]);

  return {
    syncing: enabled && state === "syncing",
    failed: enabled && state === "failed",
  };
}

/** The one line a view shows about the automatic check, if any. */
export function TaskSyncNote({ syncing, failed }: { syncing: boolean; failed: boolean }) {
  if (syncing) return <span>Checking the agent for new jobs...</span>;
  if (failed) {
    return (
      <span className="text-amber-500">
        Couldn't reach the agent to check for new jobs. Showing saved tasks.
      </span>
    );
  }
  return null;
}
